import React, { useEffect, useMemo, useState } from 'react'
import {
  Box,
  Typography,
  Grid,
  Card,
  CardContent,
  CardMedia,
  Button,
  IconButton,
  Avatar,
  Tabs,
  Tab,
  useTheme,
  useMediaQuery,
  CircularProgress,
  Divider,
  TextField,
  InputAdornment,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  Tooltip,
  keyframes,
  Chip,
} from '@mui/material'
import {
  Add,
  GridView,
  Sort,
  FilterList,
  Folder,
  Explore,
  MoreVert,
  TrendingUp,
  Schedule,
  Pattern,
  PhotoLibrary,
  Search,
  Check,
  CalendarToday,
  SortByAlpha,
  Category,
  ViewList,
  Close,
  Rocket,
  AutoAwesome,
  GridViewRounded,
  AutoAwesomeOutlined,
  AutoAwesomeRounded,
  FolderOutlined,
  FolderRounded,
  HandymanOutlined,
  HandymanRounded,
  PushPinOutlined,
  PushPinRounded,
} from '@mui/icons-material'
import { styled } from '@mui/material/styles'
import { useNavigate } from 'react-router-dom'
import useAppStore from '@/store/useAppStore'
import { shallow } from 'zustand/shallow'
import { SearchType, Topic } from '@/types'
import {
  PrivacyType,
  PrivacyTypes,
  TopicType,
  TopicTypes,
  whiteListFilterFn,
} from '@/utils/constants'
import {
  isBabylonType,
  convertToGMT,
  formattedNumber,
  getTopicNameFromTopic,
  getTopicTypeLabel,
} from '@/utils/helper'
import ReactTimeago from 'react-timeago'
import { ModernCreateModal } from '@/components/ModernCreateModal'
import { ModernCardSkeleton, RInputModal, UserAvatar } from '@/components'
import { useSubscription } from '@/context/SubscriptionContext'
import { FEATURE_KEYS } from '@/utils/subscriptions'
import CustomTimeAgo from '@/components/CustomTimeAgo'
import { useGetTopicsRevamp } from '@/hooks/api/dashboard/useGetTopicsRevamp'
import { TopicTag } from '@/components/StyledComponents'
import { RBox } from '../__styled'
import TopicStatMini from '@/components/TopicStatsMini'
import { IMAGES } from '@/assets/images'
import { useCreateTopic } from '@/hooks/api/dashboard'
import { useAuth, useLocalStorage } from '@/hooks/common'
import toast from 'react-hot-toast'
import { useTopicsListViewModel } from '@/hooks/api/dashboard/viewModels/useTopicsListViewModel'
import colors from '@/styles/colors'
import { TopicsCacheKey } from '@/utils/cacheKeys'
import { WORK_IN_PROGRESS_TYPE, WORKSPACE_TABS } from '@/utils/homePageFilterTabs'
import PricingModal from '@/pages/my-profile/components/PricingModal'

interface WorkspaceProps {
  topicType: TopicType
}

// ==================== ANIMATIONS ====================

const ANIMATION_DURATION = 350
const STAGGER_DELAY = 20
const MAX_STAGGER_DELAY = 200

const scaleIn = keyframes`
  from {
    opacity: 0;
    transform: scale(0.9);
  }
  to {
    opacity: 1;
    transform: scale(1);
  }
`

// Balanced styled components with subtle colors
const WorkspaceContainer = styled(Box)(() => ({
  background: 'linear-gradient(135deg, #fafbff 0%, #f8fafc 100%)',
  minHeight: '100vh',
}))

const HeaderSection = styled(Box)(() => ({
  background: 'linear-gradient(135deg, #ffffff 0%, #f8fafc 100%)',
  padding: '32px',
  borderBottom: '1px solid #e2e8f0',
}))

const ContentSection = styled(Box)(() => ({
  backgroundColor: '#fafbff',
  padding: '32px',
}))

const StatsCard = styled(Card)(() => ({
  background: 'linear-gradient(135deg, #ffffff 0%, #f8fafc 100%)',
  border: '1px solid #e2e8f0',
  borderRadius: '16px',
  boxShadow: '0 1px 3px rgba(15, 23, 42, 0.08), 0 1px 2px rgba(15, 23, 42, 0.04)',
  transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
  position: 'relative',
  overflow: 'hidden',

  '&::before': {
    content: '""',
    position: 'absolute',
    inset: 0,
    borderRadius: '16px',
    padding: '1px',
    background: 'linear-gradient(135deg, rgba(99, 102, 241, 0.1), rgba(168, 85, 247, 0.1))',
    WebkitMask: 'linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0)',
    WebkitMaskComposite: 'xor',
    maskComposite: 'exclude',
    opacity: 0,
    transition: 'opacity 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
  },

  '&:hover': {
    transform: 'translateY(-4px)',
    boxShadow: '0 12px 40px rgba(99, 102, 241, 0.08), 0 4px 16px rgba(15, 23, 42, 0.06)',
    borderColor: 'rgba(99, 102, 241, 0.15)',
  },

  '&:hover::before': {
    opacity: 1,
  },
}))

const ProjectCard = styled(Card)(() => ({
  backgroundColor: '#ffffff',
  border: '1px solid rgba(226, 232, 240, 0.8)',
  borderRadius: '16px',
  boxShadow: '0 1px 3px rgba(15, 23, 42, 0.08), 0 1px 2px rgba(15, 23, 42, 0.04)',
  transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
  cursor: 'pointer',
  overflow: 'hidden',
  position: 'relative',

  '&::before': {
    content: '""',
    position: 'absolute',
    inset: 0,
    borderRadius: '16px',
    padding: '1px',
    background: 'linear-gradient(135deg, rgba(99, 102, 241, 0.1), rgba(168, 85, 247, 0.1))',
    WebkitMask: 'linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0)',
    WebkitMaskComposite: 'xor',
    maskComposite: 'exclude',
    opacity: 0,
    transition: 'opacity 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
  },

  '&:hover': {
    transform: 'translateY(-6px)',
    boxShadow: '0 12px 40px rgba(99, 102, 241, 0.12), 0 4px 16px rgba(15, 23, 42, 0.08)',
    borderColor: 'rgba(99, 102, 241, 0.2)',
  },

  '&:hover::before': {
    opacity: 1,
  },
}))

const CreateButton = styled(Button)(() => ({
  background: 'linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)',
  color: '#ffffff',
  fontWeight: 600,
  fontSize: '14px',
  padding: '10px 20px',
  borderRadius: '8px',
  textTransform: 'none',
  boxShadow: '0 2px 8px rgba(99, 102, 241, 0.25)',
  border: 'none',
  '&:hover': {
    background: 'linear-gradient(135deg, #5b21b6 0%, #7c3aed 100%)',
    boxShadow: '0 4px 12px rgba(99, 102, 241, 0.35)',
    transform: 'translateY(-1px)',
  },
}))

const QuickActionButton = styled(Button)(() => ({
  background: 'linear-gradient(135deg, #ffffff 0%, #f8fafc 100%)',
  color: '#1a1a1a',
  border: '1px solid #e2e8f0',
  borderRadius: '12px',
  padding: '16px',
  cursor: 'pointer',
  transition: 'all 0.2s ease',
  textTransform: 'none',
  justifyContent: 'flex-start',
  boxShadow: '0 1px 3px rgba(0, 0, 0, 0.05)',
  '&:hover': {
    background: 'linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%)',
    borderColor: '#c7d2fe',
    boxShadow: '0 4px 12px rgba(99, 102, 241, 0.1)',
    transform: 'translateY(-1px)',
  },
}))

const CleanTabs = styled(Tabs)(() => ({
  '& .MuiTabs-indicator': {
    height: '3px',
    borderRadius: '2px',
    background: 'linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)',
  },
  '& .MuiTab-root': {
    textTransform: 'none',
    fontWeight: 500,
    fontSize: '14px',
    color: '#64748b',
    minHeight: '44px',
    '&.Mui-selected': {
      color: '#6366f1',
      fontWeight: 600,
    },
  },
}))

const quickActions = [
  {
    title: 'Create Pattern',
    description: 'Design new patterns',
    icon: <Pattern sx={{ fontSize: 20, color: '#7c3aed' }} />,
    action: 'pattern',
  },
  {
    title: `${TopicTypes[TopicType.ALBUM].title}`,
    description: 'Organize photos',
    icon: <PhotoLibrary sx={{ fontSize: 20, color: '#ec4899' }} />,
    action: 'album',
  },
  {
    title: 'Explore Designs',
    description: 'Discover inspiring patterns',
    icon: <Explore sx={{ fontSize: 20, color: '#10b981' }} />,
    action: 'document',
  },
]

// const tabs = [
//   { label: 'All', value: TopicType.ALL, icon: <Folder sx={{ fontSize: 16 }} /> },
//   { label: 'Patterns', value: TopicType.PATTERN, icon: <Pattern sx={{ fontSize: 16 }} /> },
//   { label: 'Albums', value: TopicType.ALBUM, icon: <PhotoLibrary sx={{ fontSize: 16 }} /> },
// ]

const tabs = WORKSPACE_TABS

export const CleanWorkspace: React.FC<WorkspaceProps> = ({ topicType = TopicType.ALL }) => {
  const theme = useTheme()
  const isMobile = useMediaQuery(theme.breakpoints.down('md'))
  const navigate = useNavigate()
  const { requirePro, isFeatureAvailable } = useSubscription()
  const showCollections = isFeatureAvailable(FEATURE_KEYS.COLLECTIONS)

  const { user } = useAuth()
  const createTopic = useCreateTopic(user?.userID)

  // State
  const [selectedTab, setSelectedTab] = useState(
    !topicType ? TopicType.ALL : tabs.find((el) => el.value == topicType)?.value,
  )
  const selectedTopicType = selectedTab || TopicType.ALL

  const [createModalOpen, setCreateModalOpen] = useState(false)
  const [pricingModalOpen, setPricingModalOpen] = useState(false)
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const [ewUser] = useLocalStorage('ewUser', null) as [any, unknown]
  const [currentPage, setCurrentPage] = useState(1)
  // const [hasMore, setHasMore] = useState(false)
  const [viewMode, setViewMode] = useState<'grid' | 'list'>('grid')
  const [sortBy, setSortBy] = useState<'date' | 'name' | 'type'>('date')
  const [searchQuery, setSearchQuery] = useState('')
  const [showUpgradeBanner, setShowUpgradeBanner] = useState(true)

  // Store
  const [topicList, userProfile] = useAppStore(
    (state) => [state.home.topicList, state.user.profile],
    shallow,
  )

  const [showCreatePinDialog, setShowCreatePinDialog] = useState(false)

  const patternTopicsVM = useTopicsListViewModel(TopicsCacheKey.Workspace_Patterns)
  const collectionTopicListVM = useTopicsListViewModel(TopicsCacheKey.Workspace_Collections)
  const pinCollectionsTopicListVM = useTopicsListViewModel(TopicsCacheKey.Workspace_Pins)

  const {
    getTopics: getPatternTopics,
    topicsList: patternTopics,
    topicsCount: totalPatterns,
    loading: loadingPatterns,
    loadingNextPage: loadingNextPagePatterns,
    loadNextPageData: loadNextPagePatterns,
    canLoadNextPage: canLoadNextPagePatterns,
    pagination: patternsPagination,
  } = patternTopicsVM

  const {
    getTopics: getCollectionTopics,
    topicsList: collectionTopics,
    topicsCount: totalCollections,
    loading: loadingCollections,
    loadingNextPage: loadingNextPageCollections,
    loadNextPageData: loadNextPageCollections,
    canLoadNextPage: canLoadNextPageCollections,
    pagination: collectionsPagination,
  } = collectionTopicListVM

  const {
    getTopics: getPinCollections,
    topicsList: pinCollectionTopics,
    topicsCount: totalPinCollections,
    loading: loadingPinCollections,
    loadingNextPage: loadingNextPagePinCollections,
    loadNextPageData: loadNextPagePinCollections,
    canLoadNextPage: canLoadNextPagePinCollections,
    pagination: pinsPagination,
  } = pinCollectionsTopicListVM

  const isLoading = loadingPatterns || loadingCollections || loadingPinCollections

  useEffect(() => {
    if (!patternsPagination) fetchPatterns(1)
    if (!pinsPagination) fetchPins(1)
  }, [])

  useEffect(() => {
    if (showCollections && !collectionsPagination) fetchAlbums(1)
  }, [showCollections])

  const fetchPatterns = (pageNo = 1) => {
    getPatternTopics({
      pageNo,
      privacyType: PrivacyType.ALL,
      topicType: TopicType.PATTERN,
      searchType: SearchType.loggedInUserContent,
    })
  }
  const fetchAlbums = (pageNo = 1) => {
    if (!showCollections) return

    getCollectionTopics({
      pageNo,
      privacyType: PrivacyType.ALL,
      topicType: TopicType.ALBUM,
      searchType: SearchType.loggedInUserContent,
    })
  }

  const fetchPins = (pageNo = 1) => {
    getPinCollections({
      pageNo,
      privacyType: PrivacyType.ALL,
      topicType: TopicType.PIN_COLLECTION,
      searchType: SearchType.loggedInUserContent,
    })
  }

  const hasMore = useMemo(() => {
    if (selectedTopicType == TopicType.ALL)
      return canLoadNextPagePatterns || canLoadNextPageCollections || canLoadNextPagePinCollections
    if (selectedTopicType == TopicType.PATTERN) return canLoadNextPagePatterns
    if (selectedTopicType == TopicType.ALBUM) return canLoadNextPageCollections
    if (selectedTopicType == TopicType.PIN_COLLECTION) return canLoadNextPagePinCollections
  }, [
    canLoadNextPagePatterns,
    canLoadNextPageCollections,
    canLoadNextPagePinCollections,
    selectedTopicType,
  ])

  const onClickLoadMore = () => {
    if (!loadingPatterns && canLoadNextPagePatterns) {
      loadNextPagePatterns()
    }

    if (!loadingCollections && canLoadNextPageCollections) {
      loadNextPageCollections()
    }

    if (!loadingPinCollections && canLoadNextPagePinCollections) {
      loadNextPagePinCollections()
    }
  }

  // Filter topics by category
  const filteredTopics = useMemo(() => {
    let filtered: Topic[] = []

    if (selectedTopicType == TopicType.ALL) {
      const filteredPatterns = patternTopics.filter((e) => e.firstNote.egoList?.length > 0)
      const filteredAlbums = collectionTopics.filter((e) => e.latestNote.egoList?.length > 0)
      const filteredPins = pinCollectionTopics.filter((e) => Number(e.noteCount) > 1)

      filtered = [...filteredPatterns, ...filteredAlbums, ...filteredPins]
    } else if (selectedTopicType == TopicType.PATTERN) {
      filtered = patternTopics.filter((e) => e.firstNote.egoList?.length > 0)
    } else if (selectedTopicType?.toString() == WORK_IN_PROGRESS_TYPE.toString()) {
      const filteredPatterns = patternTopics.filter((e) => e.firstNote.egoList?.length == 0)
      const filteredAlbums = collectionTopics.filter((e) => e.latestNote.egoList?.length == 0)
      // const filteredPins = pinCollections.filter((e) => Number(e.noteCount) <= 1)
      filtered = [...filteredPatterns, ...filteredAlbums]
    } else if (selectedTopicType == TopicType.ALBUM) {
      filtered = collectionTopics.filter(
        (e) => e.latestNote.egoList?.length > 0 && Number(e.photoCount) > 0,
      )
    } else if (selectedTopicType == TopicType.PIN_COLLECTION) {
      filtered = [...pinCollectionTopics]
    }

    filtered = filtered.sort((a, b) => {
      switch (sortBy) {
        case 'name':
          return a.topicName.localeCompare(b.topicName)
        case 'type':
          return a.topicType.localeCompare(b.topicType)
        case 'date':
        default:
          return Date.parse(b.dateUpdated) - Date.parse(a.dateUpdated)
      }
    })

    if (searchQuery) {
      filtered = filtered.filter((topic: Topic) => {
        const topicName = getTopicNameFromTopic(topic)
        return topicName.toLowerCase().includes(searchQuery.toLowerCase())
      })
    }

    return filtered
  }, [
    patternTopics,
    collectionTopics,
    pinCollectionTopics,
    selectedTopicType,
    searchQuery,
    showCollections,
    sortBy,
  ])

  const stats = useMemo(() => {
    return {
      total: totalPatterns + totalCollections + totalPinCollections,
      patterns: totalPatterns,
      albums: totalCollections,
      pinCollections: totalPinCollections,
      recent: 0,
    }
  }, [totalPatterns, totalCollections, totalPinCollections])

  const statCards = useMemo(
    () => [
      { icon: TrendingUp, color: '#10b981', value: stats.total, label: 'Total Projects' },
      {
        icon: Pattern,
        color: '#7c3aed',
        value: stats.patterns,
        label: TopicTypes[TopicType.PATTERN].tabTitle,
      },
      {
        icon: PhotoLibrary,
        color: '#ec4899',
        value: stats.albums,
        label: TopicTypes[TopicType.ALBUM].tabTitle,
      },
      {
        icon: PushPinRounded,
        color: '#7c3aed',
        value: stats.pinCollections,
        label: TopicTypes[TopicType.PIN_COLLECTION].tabTitle,
      },
    ],
    [stats],
  )

  const handleTabChange = (event: React.SyntheticEvent, newValue: TopicType) => {
    const proceedSelection = () => {
      const tabObj = tabs.find((el) => el.value == newValue)
      if (tabObj) {
        navigate(`/workspace/${tabObj.id}`)
        setSelectedTab(newValue)
      }
    }

    if (newValue == TopicType.ALBUM) requirePro(FEATURE_KEYS.COLLECTIONS, proceedSelection)
    else proceedSelection()
  }

  const [sortMenuAnchor, setSortMenuAnchor] = useState<null | HTMLElement>(null)

  const handleSortClick = (event: React.MouseEvent<HTMLElement>) => {
    setSortMenuAnchor(event.currentTarget)
  }

  const handleSortClose = () => {
    setSortMenuAnchor(null)
  }

  const handleSortSelect = (sort: 'date' | 'name' | 'type') => {
    setSortBy(sort)
    handleSortClose()
  }

  const onClickNewProject = () => {
    if (selectedTopicType == TopicType.PIN_COLLECTION) {
      setShowCreatePinDialog(true)
    } else {
      setCreateModalOpen(true)
    }
  }
  const onConfirmCreateNewPinCollection = async (text: string) => {
    const topicType = TopicType.PIN_COLLECTION

    const result = await createTopic.mutateAsync({
      topicName: text.trim(),
      topicType: topicType.toString(),
      allowContribution: 0,
      topicXData: 'X Data Here',
      privacyType: PrivacyType.PRIVATE,
      cliques: [],
      connections: [],
    })

    if (result.isError) {
      toast.error('Create failed. Please try again!')
    } else {
      toast.success(`${TopicTypes[TopicType.PIN_COLLECTION].title} created successfully!`)
      setShowCreatePinDialog(false)
      fetchPins(1)
    }
  }

  const handleProjectClick = (topic: Topic) => {
    const topicTypeStr = topic.topicType
    if (topicTypeStr === TopicType.PATTERN.toString()) {
      navigate(`/pattern?topicID=${topic.topicID}`)
    } else {
      navigate(`/preview?topicID=${topic.topicID}&topicType=${topicTypeStr}`)
    }
  }

  const handleCreateProject = (type: string, title: string) => {
    console.log('Creating project:', { type, title })
  }

  const getPreviewImage = (topic: Topic) => {
    // Use the same logic as the original Home component
    const egoList = isBabylonType(topic.topicType)
      ? topic.firstNote?.egoList || []
      : topic.latestNote?.egoList || []

    if (topic.topicType == TopicType.PIN_COLLECTION.toString() && topic.topicID == '16844') {
      console.log('PIN: ', JSON.stringify(topic))
    }

    if (egoList.length > 0) {
      return egoList[0].photoAlbumURL
    }

    // Fallback to photos array if egoList is empty
    if (topic.photos && topic.photos.length > 0) {
      const firstPhoto = topic.photos[0]
      return firstPhoto.photoFullURL || firstPhoto.photoThumbURL || firstPhoto.photoAlbumURL
    }

    const lastFirstEgo = topic.firstNote?.egoList?.length
      ? topic.firstNote.egoList[topic.firstNote.egoList.length - 1]
      : undefined

    const lastLatestEgo = topic.latestNote?.egoList?.length
      ? topic.latestNote.egoList[topic.latestNote.egoList.length - 1]
      : undefined

    return (
      topic.latestEgo?.photoAlbumURL ||
      lastLatestEgo?.photoAlbumURL ||
      lastLatestEgo?.photoFullURL ||
      lastFirstEgo?.photoAlbumURL ||
      lastFirstEgo?.photoFullURL ||
      undefined
    )
  }

  const getProjectTypeIcon = (topicType: string) => {
    switch (topicType) {
      case TopicType.PATTERN.toString():
        return <Pattern sx={{ fontSize: 16, color: '#7c3aed' }} />
      case TopicType.ALBUM.toString():
        return <PhotoLibrary sx={{ fontSize: 16, color: '#ec4899' }} />
      case TopicType.GALLERY.toString():
        return <GridView sx={{ fontSize: 16, color: '#16a34a' }} />
      default:
        return <Folder sx={{ fontSize: 16, color: '#64748b' }} />
    }
  }
  const renderCreateProjectCard = () => {
    return (
      <ProjectCard
        onClick={onClickNewProject}
        sx={{
          width: viewMode === 'list' ? '100%' : 220,
          height: viewMode === 'list' ? '100%' : 220,
          flexShrink: 0,
          display: 'flex',
          flexDirection: viewMode === 'list' ? 'row' : 'column',
          border: '2px dashed #c7d2fe',
          backgroundColor: 'rgba(99, 102, 241, 0.02)',
          boxShadow: 'none',
          '&:hover': {
            borderColor: '#6366f1',
            backgroundColor: 'rgba(99, 102, 241, 0.05)',
            transform: 'translateY(-6px)',
            boxShadow: '0 12px 40px rgba(99, 102, 241, 0.12), 0 4px 16px rgba(15, 23, 42, 0.08)',
          },
        }}
      >
        {/* Upper area with big plus — mirrors CardMedia's role/size */}
        <CardMedia
          sx={{
            flex: viewMode === 'list' ? 'none' : 1,
            width: viewMode === 'list' ? 240 : '100%',
            minHeight: viewMode === 'list' ? 160 : 120,
            flexShrink: 0,
            background: 'transparent',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <Box
            sx={{
              width: 56,
              height: 56,
              borderRadius: '14px',
              background: 'linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              boxShadow: '0 4px 16px rgba(99, 102, 241, 0.25)',
            }}
          >
            <Add sx={{ fontSize: 32, color: '#ffffff' }} />
          </Box>
        </CardMedia>

        {/* Bottom label — mirrors CardContent's title row */}
        <CardContent
          sx={{
            px: 2,
            py: 1.5,
            flex: viewMode === 'list' ? 1 : 'none',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            textAlign: 'center',
          }}
        >
          <Typography
            variant='subtitle1'
            fontWeight={700}
            sx={{
              color: '#6366f1',
              fontSize: '16px',
              lineHeight: '20px',
              letterSpacing: '-0.02em',
            }}
          >
            {`Create New ${TopicTypes[selectedTopicType].title}`}
          </Typography>
        </CardContent>
      </ProjectCard>
    )
  }
  const renderProjectCard = (topic: Topic, index: number) => {
    const privacyData = PrivacyTypes[Number(topic.visibility)]

    const lastFirstEgo = topic.firstNote?.egoList?.length
      ? topic.firstNote.egoList[topic.firstNote.egoList.length - 1]
      : undefined

    const lastLatestEgo = topic.latestNote?.egoList?.length
      ? topic.latestNote.egoList[topic.latestNote.egoList.length - 1]
      : undefined

    const imageURL =
      topic.latestEgo?.photoAlbumURL ||
      lastLatestEgo?.photoAlbumURL ||
      lastLatestEgo?.photoFullURL ||
      lastFirstEgo?.photoAlbumURL ||
      lastFirstEgo?.photoFullURL ||
      IMAGES.topicPreviewPlaceholder

    return (
      <ProjectCard
        key={topic.topicID}
        onClick={() => handleProjectClick(topic)}
        sx={{
          display: viewMode === 'list' ? 'flex' : 'block',
          flexDirection: viewMode === 'list' ? 'row' : 'column',
          animation: `${scaleIn} ${ANIMATION_DURATION}ms cubic-bezier(0.4, 0, 0.2, 1) ${Math.min(
            index * STAGGER_DELAY,
            MAX_STAGGER_DELAY,
          )}ms backwards`,
        }}
      >
        <CardMedia
          sx={{
            height: viewMode === 'list' ? 'auto' : 180,
            width: viewMode === 'list' ? 240 : '100%',
            minHeight: viewMode === 'list' ? 160 : 'auto',
            flexShrink: 0,
            background: 'linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            position: 'relative',
          }}
        >
          {getPreviewImage(topic) ? (
            <>
              <img
                src={getPreviewImage(topic)}
                alt={topic.topicName}
                style={{
                  width: '100%',
                  height: '100%',
                  objectFit: 'cover',
                }}
                onError={(e) => {
                  // Hide broken image and show fallback
                  e.currentTarget.style.display = 'none'
                }}
              />
              {/* Subtle overlay for better text readability */}
              <Box
                sx={{
                  position: 'absolute',
                  bottom: 0,
                  left: 0,
                  right: 0,
                  height: '30%',
                  background: 'linear-gradient(transparent, rgba(0,0,0,0.1))',
                }}
              />
            </>
          ) : (
            <Box
              sx={{
                textAlign: 'center',
                color: '#94a3b8',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                gap: 1,
              }}
            >
              <Box
                sx={{
                  p: 2,
                  borderRadius: '12px',
                  background: 'linear-gradient(135deg, #ffffff 0%, #f8fafc 100%)',
                  border: '1px solid #e2e8f0',
                }}
              >
                {getProjectTypeIcon(topic.topicType)}
              </Box>
              <Typography variant='caption' color='#64748b' fontWeight={500}>
                {TopicTypes[topic.topicType]?.title || 'Project'}
              </Typography>
            </Box>
          )}
        </CardMedia>

        <CardContent
          sx={{
            px: 2,
            flex: viewMode === 'list' ? 1 : 'none',
            display: viewMode === 'list' ? 'flex' : 'block',
            flexDirection: viewMode === 'list' ? 'column' : 'row',
          }}
        >
          {/* Title */}
          <RBox
            mb={1.5}
            display='flex'
            flexDirection='row'
            justifyContent={'space-between'}
            alignItems={'center'}
          >
            <Typography
              variant='subtitle1'
              fontWeight={700}
              sx={{
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
                color: '#0f172a',
                fontSize: '16px',
                lineHeight: '20px',
                letterSpacing: '-0.02em',
              }}
            >
              {getTopicNameFromTopic(topic)}
            </Typography>

            <Typography
              variant='caption'
              sx={{
                color: '#94a3b8',
                fontSize: '12px',
                fontWeight: 500,
              }}
            >
              <CustomTimeAgo date={topic.dateUpdated} />
            </Typography>
          </RBox>

          {/* Footer - Time and Avatar */}
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              pt: 1.5,
              mt: viewMode === 'list' ? 'auto' : 0,
              borderTop: '1px solid rgba(226, 232, 240, 0.5)',
            }}
          >
            <RBox gap={1}>
              <TopicTag
                size='small'
                themeColor={privacyData.color || colors.darkRed}
                label={privacyData.label}
              />
              <TopicTag
                size='small'
                themeColor={TopicTypes[Number(topic.topicType)].color}
                label={getTopicTypeLabel(topic.topicType)}
              />
            </RBox>
            <TopicStatMini topic={topic} />
          </Box>
        </CardContent>
      </ProjectCard>
    )
  }

  return (
    <WorkspaceContainer>
      {/* Header Section */}
      <HeaderSection>
        <Box sx={{ width: '90%', margin: '0 auto' }}>
          <Box
            sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 4 }}
          >
            <Box>
              <Typography variant='h4' fontWeight={700} color='#1a1a1a' mb={1}>
                Welcome back, {userProfile?.firstName || 'Creator'}
              </Typography>
              <Typography variant='body1' color='#64748b'>
                Manage your projects and create something new
              </Typography>
            </Box>

            <CreateButton
              startIcon={<Add />}
              onClick={() => setCreateModalOpen(true)}
              sx={{ display: { xs: 'none', md: 'flex' } }}
            >
              Create New
            </CreateButton>
          </Box>

          {/* Upgrade Banner - Shows when approaching project limit */}
          {showUpgradeBanner && stats.total >= 15 && (
            <Box
              sx={{
                position: 'relative',
                background: 'linear-gradient(135deg, #667eea 0%, #764ba2 50%, #f093fb 100%)',
                borderRadius: '20px',
                padding: '32px',
                mb: 4,
                overflow: 'hidden',
                boxShadow: '0 20px 60px rgba(102, 126, 234, 0.3), 0 8px 24px rgba(0, 0, 0, 0.15)',

                '&::before': {
                  content: '""',
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  right: 0,
                  bottom: 0,
                  background:
                    'radial-gradient(circle at 20% 50%, rgba(255, 255, 255, 0.1) 0%, transparent 50%)',
                  pointerEvents: 'none',
                },

                '&::after': {
                  content: '""',
                  position: 'absolute',
                  top: '-50%',
                  right: '-10%',
                  width: '400px',
                  height: '400px',
                  background:
                    'radial-gradient(circle, rgba(255, 255, 255, 0.15) 0%, transparent 70%)',
                  borderRadius: '50%',
                  pointerEvents: 'none',
                },
              }}
            >
              <IconButton
                onClick={() => setShowUpgradeBanner(false)}
                sx={{
                  position: 'absolute',
                  top: 16,
                  right: 16,
                  zIndex: 2,
                  color: 'rgba(255, 255, 255, 0.9)',
                  backgroundColor: 'rgba(255, 255, 255, 0.1)',
                  backdropFilter: 'blur(10px)',
                  width: 36,
                  height: 36,
                  transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
                  '&:hover': {
                    backgroundColor: 'rgba(255, 255, 255, 0.2)',
                    transform: 'rotate(90deg)',
                  },
                }}
              >
                <Close fontSize='small' />
              </IconButton>

              <Box sx={{ position: 'relative', zIndex: 1 }}>
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    flexWrap: { xs: 'wrap', md: 'nowrap' },
                    gap: 3,
                  }}
                >
                  {/* Left side - Icon + Content */}
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 3, flex: 1 }}>
                    <Box
                      sx={{
                        width: 64,
                        height: 64,
                        borderRadius: '16px',
                        background: 'rgba(255, 255, 255, 0.15)',
                        backdropFilter: 'blur(10px)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        border: '1px solid rgba(255, 255, 255, 0.2)',
                        boxShadow: '0 4px 16px rgba(0, 0, 0, 0.1)',
                      }}
                    >
                      <Rocket sx={{ fontSize: 32, color: '#ffffff' }} />
                    </Box>

                    <Box>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                        <Typography
                          variant='h5'
                          sx={{
                            color: '#ffffff',
                            fontWeight: 800,
                            letterSpacing: '-0.02em',
                            fontSize: { xs: '20px', md: '24px' },
                          }}
                        >
                          You're on fire! 🔥
                        </Typography>
                        <AutoAwesome sx={{ color: '#fbbf24', fontSize: 24 }} />
                      </Box>
                      <Typography
                        variant='body1'
                        sx={{
                          color: 'rgba(255, 255, 255, 0.95)',
                          fontSize: { xs: '14px', md: '16px' },
                          fontWeight: 500,
                          lineHeight: 1.6,
                          maxWidth: '500px',
                        }}
                      >
                        You have {stats.total} amazing projects! Upgrade to Pro for unlimited
                        projects, advanced templates, and priority support.
                      </Typography>
                    </Box>
                  </Box>

                  {/* Right side - CTA Button */}
                  <Button
                    startIcon={<Rocket />}
                    onClick={() => {
                      // Navigate to upgrade page or show modal
                      setPricingModalOpen(true)
                    }}
                    sx={{
                      background: 'rgba(255, 255, 255, 0.95)',
                      color: '#667eea',
                      fontWeight: 700,
                      fontSize: '15px',
                      padding: '12px 32px',
                      borderRadius: '12px',
                      textTransform: 'none',
                      boxShadow: '0 4px 16px rgba(0, 0, 0, 0.15), 0 2px 4px rgba(0, 0, 0, 0.1)',
                      border: 'none',
                      letterSpacing: '-0.01em',
                      transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                      marginRight: '40px',

                      '&:hover': {
                        background: '#ffffff',
                        transform: 'translateY(-2px)',
                        boxShadow: '0 8px 24px rgba(0, 0, 0, 0.2), 0 4px 8px rgba(0, 0, 0, 0.15)',
                      },

                      '&:active': {
                        transform: 'translateY(0)',
                      },
                    }}
                  >
                    Upgrade to Pro
                  </Button>
                </Box>
              </Box>
            </Box>
          )}

          {/* Stats */}
          <Grid container spacing={3} sx={{ mb: 4 }}>
            {statCards.map(({ icon: Icon, color, value, label }, i) => (
              <Grid item xs={12} sm={6} md key={i}>
                <StatsCard>
                  <CardContent sx={{ textAlign: 'center', p: 3 }}>
                    <Icon sx={{ fontSize: 24, mb: 1, color }} />
                    <Typography variant='h5' fontWeight={700} color='#1a1a1a'>
                      {formattedNumber(value)}
                    </Typography>
                    <Typography variant='body2' color='#64748b'>
                      {label}
                    </Typography>
                  </CardContent>
                </StatsCard>
              </Grid>
            ))}
          </Grid>

          {/* Quick Actions */}
          <Box>
            <Typography variant='h6' fontWeight={600} mb={3} color='#1a1a1a'>
              Quick Actions
            </Typography>
            <Grid container spacing={2}>
              {quickActions.map((action, index) => (
                <Grid item xs={12} md={4} key={index}>
                  <QuickActionButton
                    fullWidth
                    onClick={() => {
                      if (action.action === 'pattern') {
                        setCreateModalOpen(true)
                      } else if (action.action === 'album') {
                        requirePro(FEATURE_KEYS.COLLECTIONS, () => setSelectedTab(TopicType.ALBUM))
                      } else if (action.action === 'document') {
                        navigate('/discover/pattern')
                      }
                    }}
                    startIcon={action.icon}
                  >
                    <Box sx={{ textAlign: 'left' }}>
                      <Typography variant='subtitle2' fontWeight={600} color='#1a1a1a'>
                        {action.title}
                      </Typography>
                      <Typography variant='caption' color='#64748b'>
                        {action.description}
                      </Typography>
                    </Box>
                  </QuickActionButton>
                </Grid>
              ))}
            </Grid>
          </Box>
        </Box>
      </HeaderSection>

      {/* Content Section */}
      <ContentSection>
        <Box sx={{ width: '90%', margin: '0 auto' }}>
          {/* Header with Search */}
          <Box sx={{ mb: 3 }}>
            <Box
              sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3 }}
            >
              <Typography variant='h5' fontWeight={700} color='#1a1a1a' letterSpacing='-0.02em'>
                Your Projects
              </Typography>

              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                {/* View Mode Toggle */}
                <Tooltip title={viewMode === 'grid' ? 'Grid View' : 'List View'} arrow>
                  <IconButton
                    size='small'
                    onClick={() => setViewMode(viewMode === 'grid' ? 'list' : 'grid')}
                    sx={{
                      width: 36,
                      height: 36,
                      borderRadius: '10px',
                      backgroundColor:
                        viewMode === 'grid' ? 'rgba(99, 102, 241, 0.1)' : 'transparent',
                      color: viewMode === 'grid' ? '#6366f1' : '#64748b',
                      border: '1px solid',
                      borderColor: viewMode === 'grid' ? 'rgba(99, 102, 241, 0.2)' : '#e2e8f0',
                      transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
                      '&:hover': {
                        backgroundColor: 'rgba(99, 102, 241, 0.1)',
                        borderColor: 'rgba(99, 102, 241, 0.2)',
                        color: '#6366f1',
                      },
                    }}
                  >
                    {viewMode === 'grid' ? (
                      <GridView fontSize='small' />
                    ) : (
                      <ViewList fontSize='small' />
                    )}
                  </IconButton>
                </Tooltip>

                {/* Sort Button with Menu */}
                <Tooltip title='Sort by' arrow>
                  <IconButton
                    size='small'
                    onClick={handleSortClick}
                    sx={{
                      width: 36,
                      height: 36,
                      borderRadius: '10px',
                      backgroundColor: sortMenuAnchor ? 'rgba(99, 102, 241, 0.1)' : 'transparent',
                      color: sortMenuAnchor ? '#6366f1' : '#64748b',
                      border: '1px solid',
                      borderColor: sortMenuAnchor ? 'rgba(99, 102, 241, 0.2)' : '#e2e8f0',
                      transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
                      '&:hover': {
                        backgroundColor: 'rgba(99, 102, 241, 0.1)',
                        borderColor: 'rgba(99, 102, 241, 0.2)',
                        color: '#6366f1',
                      },
                    }}
                  >
                    <Sort fontSize='small' />
                  </IconButton>
                </Tooltip>

                <Menu
                  anchorEl={sortMenuAnchor}
                  open={Boolean(sortMenuAnchor)}
                  onClose={handleSortClose}
                  PaperProps={{
                    sx: {
                      borderRadius: '12px',
                      border: '1px solid #e2e8f0',
                      boxShadow: '0 8px 24px rgba(0, 0, 0, 0.12)',
                      mt: 1,
                      minWidth: 200,
                    },
                  }}
                >
                  <MenuItem
                    selected={sortBy === 'date'}
                    onClick={() => handleSortSelect('date')}
                    sx={{
                      borderRadius: '8px',
                      mx: 1,
                      my: 0.5,
                      '&.Mui-selected': {
                        backgroundColor: 'rgba(99, 102, 241, 0.08)',
                      },
                    }}
                  >
                    <ListItemIcon>
                      {sortBy === 'date' ? (
                        <Check sx={{ color: '#6366f1' }} fontSize='small' />
                      ) : (
                        <CalendarToday fontSize='small' />
                      )}
                    </ListItemIcon>
                    <ListItemText>Date Updated</ListItemText>
                  </MenuItem>
                  <MenuItem
                    selected={sortBy === 'name'}
                    onClick={() => handleSortSelect('name')}
                    sx={{
                      borderRadius: '8px',
                      mx: 1,
                      my: 0.5,
                      '&.Mui-selected': {
                        backgroundColor: 'rgba(99, 102, 241, 0.08)',
                      },
                    }}
                  >
                    <ListItemIcon>
                      {sortBy === 'name' ? (
                        <Check sx={{ color: '#6366f1' }} fontSize='small' />
                      ) : (
                        <SortByAlpha fontSize='small' />
                      )}
                    </ListItemIcon>
                    <ListItemText>Name</ListItemText>
                  </MenuItem>
                  <MenuItem
                    selected={sortBy === 'type'}
                    onClick={() => handleSortSelect('type')}
                    sx={{
                      borderRadius: '8px',
                      mx: 1,
                      my: 0.5,
                      '&.Mui-selected': {
                        backgroundColor: 'rgba(99, 102, 241, 0.08)',
                      },
                    }}
                  >
                    <ListItemIcon>
                      {sortBy === 'type' ? (
                        <Check sx={{ color: '#6366f1' }} fontSize='small' />
                      ) : (
                        <Category fontSize='small' />
                      )}
                    </ListItemIcon>
                    <ListItemText>Type</ListItemText>
                  </MenuItem>
                </Menu>
              </Box>
            </Box>

            {/* Search Bar */}
            <TextField
              fullWidth
              placeholder='Search projects...'
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position='start'>
                    <Search sx={{ color: '#94a3b8', fontSize: 20 }} />
                  </InputAdornment>
                ),
              }}
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: '12px',
                  backgroundColor: '#ffffff',
                  fontSize: '14px',
                  fontWeight: 500,
                  transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
                  '& fieldset': {
                    borderColor: '#e2e8f0',
                  },
                  '&:hover fieldset': {
                    borderColor: '#c7d2fe',
                  },
                  '&.Mui-focused fieldset': {
                    borderColor: '#6366f1',
                    borderWidth: '2px',
                  },
                },
                '& .MuiInputBase-input': {
                  padding: '12px 14px 12px 0',
                },
              }}
            />
          </Box>

          {/* Tabs */}
          <CleanTabs
            value={selectedTab}
            onChange={handleTabChange}
            variant={isMobile ? 'scrollable' : 'standard'}
            sx={{ mb: 4 }}
          >
            {tabs.map((tab) => (
              <Tab
                key={tab.value}
                label={
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    {tab.value == selectedTab ? tab.activeIcon : tab.defaultIcon}
                    {tab.label}
                  </Box>
                }
                value={tab.value}
              />
            ))}
          </CleanTabs>

          {/* Projects Grid */}
          {(loadingPatterns || loadingCollections || loadingPinCollections) &&
          filteredTopics?.length == 0 ? (
            <ModernCardSkeleton count={8} />
          ) : filteredTopics?.length === 0 ? (
            <Box sx={{ textAlign: 'center', py: 8 }}>
              <Folder sx={{ fontSize: 48, mb: 2, color: '#cbd5e1' }} />
              <Typography variant='h6' fontWeight={500} mb={1} color='#64748b'>
                {`No projects ${searchQuery.length == 0 ? 'yet' : 'found'!}`}
              </Typography>

              {searchQuery.length == 0 && (
                <>
                  <Typography variant='body2' mb={3} color='#94a3b8'>
                    Create your first project to get started
                  </Typography>
                  <CreateButton startIcon={<Add />} onClick={onClickNewProject}>
                    Create Project
                  </CreateButton>
                </>
              )}
            </Box>
          ) : (
            <>
              <Grid container spacing={viewMode === 'list' ? 2 : 3}>
                {(selectedTopicType == TopicType.PIN_COLLECTION ||
                  selectedTopicType == TopicType.PATTERN) &&
                  searchQuery?.length == 0 && (
                    <Grid
                      item
                      xs={12}
                      sm={viewMode === 'list' ? 12 : 6}
                      md={viewMode === 'list' ? 12 : 4}
                      lg={viewMode === 'list' ? 12 : 3}
                      key={'1001'}
                      sx={{
                        display: 'flex',
                        justifyContent: viewMode === 'list' ? 'stretch' : 'center',
                        alignItems: viewMode === 'list' ? 'stretch' : 'center',
                      }}
                    >
                      {renderCreateProjectCard()}
                    </Grid>
                  )}

                {filteredTopics.map((topic, index) => (
                  <Grid
                    item
                    xs={12}
                    sm={viewMode === 'list' ? 12 : 6}
                    md={viewMode === 'list' ? 12 : 4}
                    lg={viewMode === 'list' ? 12 : 3}
                    key={topic.topicID}
                  >
                    {renderProjectCard(topic, index)}
                  </Grid>
                ))}
              </Grid>

              {/* Load More Button */}
              {hasMore && (
                <Box sx={{ textAlign: 'center', mt: 4 }}>
                  <CreateButton
                    onClick={onClickLoadMore}
                    disabled={isLoading}
                    sx={{ minWidth: '200px' }}
                  >
                    {isLoading ? (
                      <CircularProgress size={20} sx={{ color: '#ffffff' }} />
                    ) : (
                      'Load More'
                    )}
                  </CreateButton>
                </Box>
              )}
            </>
          )}
        </Box>
      </ContentSection>

      {/* Create Modal */}
      <ModernCreateModal
        open={createModalOpen}
        onClose={() => setCreateModalOpen(false)}
        onCreateProject={handleCreateProject}
      />

      <RInputModal
        open={showCreatePinDialog}
        maxLength={30}
        onClose={() => setShowCreatePinDialog(false)}
        title={`Create New ${TopicTypes[TopicType.PIN_COLLECTION].title}`}
        text={''}
        placeholder={`Enter ${TopicTypes[TopicType.PIN_COLLECTION].title} Title`}
        description=''
        submitText='Save'
        onSubmit={onConfirmCreateNewPinCollection}
        shouldCloseOnSubmit={false}
        showLoading={createTopic.isLoading}
      />
      <PricingModal open={pricingModalOpen} onClose={() => setPricingModalOpen(false)} userId={ewUser?.userID} userEmail={ewUser?.userName} />
    </WorkspaceContainer>
  )
}

export default CleanWorkspace
